// Owner contact share modal — method picker, value input, confirm
import { useState } from "react";
import { Phone, Mail, MessageSquare, X } from "lucide-react";
import { Button } from "@/components/shared";
import { ApiClient } from "@/services/api";

interface Props {
  leadId: string;
  existingShares?: Array<{ id: string; method: string; value: string; shared_at: string }>;
  onClose: () => void;
  onShared: () => void;
}

const METHODS = [
  { key: "phone", label: "Phone", icon: Phone, placeholder: "+27 XX XXX XXXX" },
  { key: "email", label: "Email", icon: Mail, placeholder: "your@email.co.za" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageSquare, placeholder: "+27 XX XXX XXXX" },
] as const;

export function ContactShareModal({ leadId, existingShares = [], onClose, onShared }: Props) {
  const [method, setMethod] = useState<"phone" | "email" | "whatsapp">("phone");
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const alreadyShared = existingShares.map((s) => s.method);

  const handleShare = async () => {
    if (!value.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const api = new ApiClient();
      await api.post("/contacts/share", { lead_id: leadId, method, value: value.trim() });
      setSuccess(true);
      setTimeout(() => { onShared(); onClose(); }, 1200);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-midnight border border-white/15 rounded-xl p-6 max-w-md w-full mx-4 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">Share Contact Details</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <p className="text-sm text-white/50">
          Choose how you'd like this agent to contact you. You can revoke access at any time.
        </p>

        {success ? (
          <div className="text-center py-6">
            <p className="text-green-400 font-medium text-lg">✓ Contact shared</p>
            <p className="text-xs text-white/40 mt-1">The agent can now see your contact details.</p>
          </div>
        ) : (
          <>
            {/* Method picker */}
            <div className="flex gap-2">
              {METHODS.map((m) => {
                const Icon = m.icon;
                const isShared = alreadyShared.includes(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => { setMethod(m.key); setValue(""); }}
                    className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                      method === m.key
                        ? "border-sun bg-sun/10 text-sun"
                        : "border-white/10 text-white/50 hover:border-white/30"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-medium">{m.label}</span>
                    {isShared && <span className="text-[9px] text-green-400">shared</span>}
                  </button>
                );
              })}
            </div>

            {/* Value input */}
            <input
              type={method === "email" ? "email" : "tel"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={METHODS.find((m) => m.key === method)?.placeholder}
              className="w-full bg-white/5 border border-white/20 rounded-lg p-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-sun/50"
              autoFocus
            />

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleShare} disabled={!value.trim() || submitting}>
                {submitting ? "Sharing..." : "Share Contact"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
